import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ChevronIcon } from '../components/icons';
import {
  useSubscribeTopicMutation,
  useTopicSubscriptionsQuery,
  useTopicsQuery,
  useUnsubscribeTopicMutation,
} from '../hooks/use-topics-query';

// Topics screen — uses the same rail + row + follow-pill primitives as the
// Explore screen so the two surfaces feel like one system. Click a row to
// open the tag feed; tap the inline Follow / Following pill to (un)subscribe
// without leaving the page.
export function Topics() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const topics = useTopicsQuery();
  const subs = useTopicSubscriptionsQuery();
  const subscribe = useSubscribeTopicMutation();
  const unsubscribe = useUnsubscribeTopicMutation();

  const subscribedSet = useMemo(
    () => new Set((subs.data ?? []).map((s) => s.topicSlug)),
    [subs.data],
  );

  const followedCount = subs.data?.length ?? 0;
  const list = topics.data ?? [];
  const pending = subscribe.isPending || unsubscribe.isPending;

  return (
    <div className="nw-app nw-app--two">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.topics.sidebar.label')}</div>
        <div style={{ padding: '6px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('web.topics.sidebar.followedCount', { count: followedCount })}
        </div>
        <div className="nw-side__divider" />
        <div className="nw-side__label">{t('web.topics.sidebar.quickLinks')}</div>
        <button type="button" className="nw-side__item" onClick={() => navigate('/home')}>
          <span className="nw-side__item-label">{t('web.topics.sidebar.backToFeed')}</span>
        </button>
        <button type="button" className="nw-side__item" onClick={() => navigate('/explore')}>
          <span className="nw-side__item-label">{t('web.topics.sidebar.exploreChannels')}</span>
        </button>
      </aside>

      <section className="nw-feed">
        <div className="nw-feed__head">
          <div>
            <h1 className="nw-feed__title">{t('web.topics.title')}</h1>
            <p className="nw-feed__sub">{t('web.topics.subtitle')}</p>
          </div>
        </div>

        {topics.isLoading && (
          <div className="nw-feed__loading">
            <div className="nw-feed__loading-spinner" aria-hidden />
            {t('web.topics.loading')}
          </div>
        )}

        {!topics.isLoading && list.length === 0 && (
          <div className="nw-feed__empty">{t('web.topics.empty')}</div>
        )}

        {list.length > 0 && (
          <div className="nw-rail">
            <div className="nw-rail__head">
              <div>
                <div className="nw-section-eyebrow">{t('web.topics.section.eyebrow')}</div>
                <h2 className="nw-rail__title">{t('web.topics.section.count', { count: list.length })}</h2>
                <p className="nw-rail__sub">{t('web.topics.section.subtitle')}</p>
              </div>
            </div>
            <div className="nw-rail__list">
              {list.map((topic) => {
                const on = subscribedSet.has(topic.slug);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    className="nw-explore-row"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('[data-follow]')) return;
                      navigate(`/tag/${encodeURIComponent(topic.slug)}`);
                    }}
                  >
                    <span
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 14,
                        background: 'color-mix(in oklab, var(--accent), transparent 86%)',
                        color: 'var(--accent)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'var(--font-display)',
                        fontWeight: 800,
                        fontSize: 22,
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      #
                    </span>
                    <div className="nw-explore-row__body">
                      <div className="nw-explore-row__name">{topic.label}</div>
                      {topic.description && <div className="nw-explore-row__desc">{topic.description}</div>}
                      <div className="nw-explore-row__meta">#{topic.slug}</div>
                    </div>
                    <span
                      data-follow
                      role="button"
                      tabIndex={0}
                      className={`nw-channel-row__follow ${on ? 'is-following' : ''}`}
                      aria-disabled={pending}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pending) return;
                        if (on) unsubscribe.mutate(topic.slug);
                        else subscribe.mutate(topic.slug);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (pending) return;
                        if (on) unsubscribe.mutate(topic.slug);
                        else subscribe.mutate(topic.slug);
                      }}
                    >
                      {on ? t('web.explore.follow.following') : t('web.explore.follow.follow')}
                    </span>
                    <ChevronIcon dir="right" size={16} stroke={2} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
